/**
 * @file firebase.js
 * @description Functions for working with Firebase.
 */

import { initializeApp } from "firebase/app";
import { getPerformance } from "firebase/performance";
import { getAnalytics, isSupported } from "firebase/analytics";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  addDoc,
  setDoc,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getUTCTimeFromLocalHourAndTimezone } from "./utils/dateUtils";
import { obscureEmail } from "./utils/string_utils";
import { getLCP, getFID, getCLS } from "web-vitals"; // for performance metrics
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

// // AppCheck enabled for production only
// let initializeAppCheck, ReCaptchaV3Provider;
// if (process.env.NODE_ENV === "production") {
//   try {
//     const appCheckModule = require("firebase/app-check");
//     initializeAppCheck = appCheckModule.initializeAppCheck;
//     ReCaptchaV3Provider = appCheckModule.ReCaptchaV3Provider;
//   } catch (error) {
//     console.warn('Failed to load Firebase App Check:', error);
//   }
// }

getCLS(console.log);
getFID(console.log);
getLCP(console.log);

// Dynamic authDomain configuration for preview channels
const getAuthDomain = () => {
  const hostname = window.location.hostname;
  
  // For Firebase preview channels (format: project--channel-id.web.app)
  if (hostname.includes('--') && hostname.endsWith('.web.app')) {
    return hostname;
  }
  
  // For custom domains and production
  return "memorize.life";
};

const firebaseConfig = {
  apiKey: "AIzaSyBZh6UHfbJC9Vb21lBYNK82QKzkJ2-D4BE",
  authDomain: getAuthDomain(),
  databaseURL: "https://memorize-life-default-rtdb.firebaseio.com",
  projectId: "memorize-life",
  storageBucket: "memorize-life.appspot.com",
  messagingSenderId: "608680780787",
  appId: "1:608680780787:web:58d852024036f7715d2886",
  measurementId: "G-D3RN5DTQD9",
};

const app = initializeApp(firebaseConfig);

// Initialize App Check only in production
let appCheck = null;
if (process.env.NODE_ENV === "production" && initializeAppCheck && ReCaptchaV3Provider) {
  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider("6LfbdbklAAAAABVTPDEG8WXZ8Q5hMZzVowpae6sO"),
      isTokenAutoRefreshEnabled: true,
    });
    console.log('Firebase App Check initialized for production');
  } catch (error) {
    console.warn('Failed to initialize Firebase App Check:', error);
  }
} else {
  console.log('Firebase App Check disabled in development mode');
}

export { appCheck };

export const perf = getPerformance(app);

// Initialize Analytics (only in browser environment and if supported)
let analytics = null;
if (typeof window !== 'undefined') {
  isSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
      console.log('Firebase Analytics initialized');
    } else {
      console.log('Firebase Analytics not supported in this environment');
    }
  }).catch((error) => {
    console.warn('Failed to initialize Firebase Analytics:', error);
  });
}

export { analytics };

export const db = getFirestore(app);
export const auth = getAuth(app);

export const storage = getStorage(app);

// connect to emulators
// if (process.env.REACT_APP_ENV === "development") {
//   connectFirestoreEmulator(db, "localhost", 8080);
// }

/**
 * Check if a given user's email belongs to a user in the database.
 * @param db {firebase.firestore.Firestore}
 * @param email {string}
 * @returns {Promise<boolean>}
 */
export async function checkIfEmailIsAUser(db, email) {
  // query users based on email
  const usersRef = collection(db, "users");
  const usersQuery = query(usersRef, where("email", "==", email));
  const usersQuerySnapshot = await getDocs(usersQuery);
  return !usersQuerySnapshot.empty;
}

/**
 * Get the UID of a user from their email. If there are multiple users with the
 * same email, return the one that is not the ignoreID.
 * @param db {Firestore}
 * @param email {string}
 * @param ignoreID {string}
 * @returns {Promise<*|null>}
 */
export async function getUIDfromEmail(db, email, ignoreID = null) {
  // query users based on email
  const usersRef = collection(db, "users");
  const usersQuery = query(usersRef, where("email", "==", email));
  const usersQuerySnapshot = await getDocs(usersQuery);
  if (usersQuerySnapshot.empty) {
    return null;
  } else if (usersQuerySnapshot.size > 1) {
    // if there are multiple users with the same email, return the one that is
    // not the ignoreID
    for (const doc of usersQuerySnapshot.docs) {
      if (doc.id !== ignoreID) {
        return doc.id;
      }
    }
  } else {
    return usersQuerySnapshot.docs[0].id;
  }
}

/**
 * Set up the email times document for a user (only used the first time a user
 * is added to the database).
 * @param db {Firestore}
 * @param gmtEmailTime {string} e.g. "11,0"
 * @param user {firebase.User}
 * @returns {Promise<void>}
 */
async function setEmailTimesDocumentFirstTime(db, gmtEmailTime, user) {
  const emailTimesDoc = doc(db, "email_times", gmtEmailTime);
  const emailTimesUserDoc = doc(
    db,
    "email_times",
    gmtEmailTime,
    "users",
    user.uid
  );
  
  // Check if documents exist first
  const emailTimesDocSnapshot = await getDoc(emailTimesDoc);
  const emailTimesUserDocSnapshot = await getDoc(emailTimesUserDoc);
  
  // Use batch write to atomically create both documents if needed
  const batch = writeBatch(db);
  if (!emailTimesDocSnapshot.exists()) {
    batch.set(emailTimesDoc, {});
  }
  if (!emailTimesUserDocSnapshot.exists()) {
    batch.set(emailTimesUserDoc, {});
  }
  
  // Only commit if there are operations to perform
  if (!emailTimesDocSnapshot.exists() || !emailTimesUserDocSnapshot.exists()) {
    await batch.commit();
  }
}

/**
 * Create a user in the database if they don't already exist. Also upgrades a temporary user to a permanent user.
 * @param db {Firestore}
 * @param user {firebase.User}
 * @returns {Promise<void>}
 */
export async function createUserInDatabaseIfDoesNotExist(db, user) {
  if (!user) {
    return;
  }
  // given an auth.user object, create a user in the database if it doesn't yet exist
  const userDoc = doc(db, "users", user.uid);
  const userDocSnapshot = await getDoc(userDoc);
  if (!userDocSnapshot.exists()) {
    // check if the user was already added as a temporary user, if so, transfer
    // the challenges from the temporary user to the new user at the end
    const oldUID = await getUIDfromEmail(db, user.email);
    const gmtEmailTime = getUTCTimeFromLocalHourAndTimezone(
      7,
      Intl.DateTimeFormat().resolvedOptions().timeZone
    ).toString();
    await setDoc(userDoc, {
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      emailTime: 7,
      gmtEmailTime: gmtEmailTime,
      temporary: false,
      bibleLanguage: "english",
    });
    await setEmailTimesDocumentFirstTime(db, gmtEmailTime, user);
    if (oldUID !== null) {
      // get all challenges in the temporary user's challenges collection
      const challengesCollection = collection(
        db,
        "users",
        oldUID,
        "challenges"
      );
      const challengesCollectionSnapshot = await getDocs(challengesCollection);
      const challenges = [];
      challengesCollectionSnapshot.forEach((doc) => {
        challenges.push(doc);
      });
      
      // Get challenge documents to determine reading_portions for each
      const challengeDocs = await Promise.all(
        challenges.map((challenge) => 
          getDoc(doc(db, "challenges", challenge.id))
        )
      );
      
      // Use batch write to atomically migrate all challenges and cleanup old user
      const batch = writeBatch(db);
      
      // Migrate each challenge to the new user
      challenges.forEach((challenge, index) => {
        const challengeDoc = challengeDocs[index];
        const challengeId = challenge.id;
        const challengeData = challenge.data();
        
        // Add challenge to new user's challenges collection with progress data
        const newUserChallengeRef = doc(db, "users", user.uid, "challenges", challengeId);
        batch.set(newUserChallengeRef, {
          ...challengeData,
          progress: challengeData.progress || "0".repeat(challengeDoc.exists() ? challengeDoc.data().reading_portions : 0),
          emailUpdates: challengeData.emailUpdates !== undefined ? challengeData.emailUpdates : true,
        });
        
        // Add new user to challenge's users list
        const newChallengeUserRef = doc(db, "challenges", challengeId, "users", user.uid);
        batch.set(newChallengeUserRef, {});
        
        // Remove old user from challenge's users list
        const oldChallengeUserRef = doc(db, "challenges", challengeId, "users", oldUID);
        batch.delete(oldChallengeUserRef);
        
        // Remove challenge from old user's challenges collection
        const oldUserChallengeRef = doc(db, "users", oldUID, "challenges", challengeId);
        batch.delete(oldUserChallengeRef);
      });
      
      // Remove old user from email_times collection
      const oldUserDoc = doc(db, "users", oldUID);
      const oldUserDocSnapshot = await getDoc(oldUserDoc);
      if (oldUserDocSnapshot.exists()) {
        const oldUserGmtEmailTime = oldUserDocSnapshot.data().gmtEmailTime;
        if (oldUserGmtEmailTime) {
          const oldEmailTimeUserRef = doc(db, "email_times", oldUserGmtEmailTime, "users", oldUID);
          batch.delete(oldEmailTimeUserRef);
        }
      }
      
      // Delete the old user document
      batch.delete(oldUserDoc);
      
      // Commit all operations atomically
      await batch.commit();
    }
  } else {
    const userData = userDocSnapshot.data();
    // check if userData contains all the keys necessary
    const userDataKeys = [
      "email",
      "displayName",
      "photoURL",
      "timezone",
      "emailTime",
      "gmtEmailTime",
      "temporary",
      "bibleLanguage",
    ];
    const userDataKeysExist = userDataKeys.every((key) =>
      userData.hasOwnProperty(key)
    );
    if (!userDataKeysExist) {
      // Only add the truly missing keys, don't overwrite existing ones
      const updatedFields = {};
      
      if (!userData.hasOwnProperty("email")) {
        updatedFields["email"] = user.email;
      }
      if (!userData.hasOwnProperty("displayName")) {
        updatedFields["displayName"] = user.displayName;
      }
      if (!userData.hasOwnProperty("photoURL")) {
        updatedFields["photoURL"] = user.photoURL;
      }
      if (!userData.hasOwnProperty("timezone")) {
        updatedFields["timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      if (!userData.hasOwnProperty("emailTime")) {
        updatedFields["emailTime"] = 7;
      }
      if (!userData.hasOwnProperty("temporary")) {
        updatedFields["temporary"] = false;
      }
      if (!userData.hasOwnProperty("bibleLanguage")) {
        updatedFields["bibleLanguage"] = "english";
      }
      
      // Handle gmtEmailTime specially as it depends on emailTime and timezone
      if (!userData.hasOwnProperty("gmtEmailTime")) {
        const emailTime = userData.hasOwnProperty("emailTime") ? userData["emailTime"] : updatedFields["emailTime"];
        const timezone = userData.hasOwnProperty("timezone") ? userData["timezone"] : updatedFields["timezone"];
        const gmtEmailTime = getUTCTimeFromLocalHourAndTimezone(emailTime, timezone).toString();
        updatedFields["gmtEmailTime"] = gmtEmailTime;
        await setEmailTimesDocumentFirstTime(db, gmtEmailTime, user);
      }

      // Always upgrade the user to a permanent user
      // updatedFields["temporary"] = false;
      
      // Only update if there are actually missing fields
      if (Object.keys(updatedFields).length > 0) {
        await setDoc(userDoc, updatedFields, { merge: true });
      }
    }
  }
}

/**
 * Create a temporary user from an email address.
 * @param db
 * @param email
 * @returns {Promise<*>}
 */
export async function createTemporaryUserFromEmail(db, email) {
  // create a temporary user. return the user ID
  const gmtEmailTime = getUTCTimeFromLocalHourAndTimezone(
    7,
    Intl.DateTimeFormat().resolvedOptions().timeZone
  ).toString();
  const userData = {
    email: email,
    displayName: obscureEmail(email),
    photoURL: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    emailTime: 7,
    gmtEmailTime: gmtEmailTime,
    temporary: true,
    bibleLanguage: "english",
  };
  const docRef = await addDoc(collection(db, "users"), userData);
  
  // Use batch write to atomically add the user to the email_times collection
  const emailTimesDoc = doc(db, "email_times", gmtEmailTime);
  const emailTimesUserDoc = doc(
    db,
    "email_times",
    gmtEmailTime,
    "users",
    docRef.id
  );
  
  const emailTimesDocSnapshot = await getDoc(emailTimesDoc);
  const emailTimesUserDocSnapshot = await getDoc(emailTimesUserDoc);
  
  const batch = writeBatch(db);
  if (!emailTimesDocSnapshot.exists()) {
    batch.set(emailTimesDoc, {});
  }
  if (!emailTimesUserDocSnapshot.exists()) {
    batch.set(emailTimesUserDoc, {});
  }
  
  // Only commit if there are operations to perform
  if (!emailTimesDocSnapshot.exists() || !emailTimesUserDocSnapshot.exists()) {
    await batch.commit();
  }

  return docRef.id;
}


/**
 * Update archived status for a user's challenge
 * When archiving, disables email updates for the challenge
 * When unarchiving, re-enables email updates for the challenge
 * @param {object} db - Firestore database instance
 * @param {string} uid - User ID
 * @param {string} challengeID - Challenge ID
 * @param {boolean} archived - Archived status
 */
export async function updateArchivedStatus(db, uid, challengeID, archived) {
  const updateData = {
    archived: archived,
  };
  
  // When archiving, disable email updates; when unarchiving, re-enable them
  updateData.emailUpdates = !archived;
  
  await setDoc(
    doc(db, "users", uid, "challenges", challengeID),
    updateData,
    { merge: true }
  );
}
